import { Dropdown, Checkbox, Input, Spin } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { ChevronDown } from "lucide-react";
import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { mixGetV1, removeAccents } from "../../../services/mixin";

interface StaffOption {
    id: number;
    name: string;
}

interface Props {
    value: number[];
    onChange: (ids: number[]) => void;
}

/** Bỏ dấu và hạ chữ thường để gõ "duc" vẫn ra "Đức". */
const normalize = (s: string) => removeAccents(s.toLowerCase()).replace(/đ/g, "d");

const AssignFilter = ({ value, onChange }: Props) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [keyword, setKeyword] = useState("");
    const [staffList, setStaffList] = useState<StaffOption[]>([]);
    const [loadingStaff, setLoadingStaff] = useState(false);

    useEffect(() => {
        setLoadingStaff(true);
        mixGetV1('/api/v1/be/staffs', { type_user: 1, limit: 200 })
            .then((res: any) => {
                const items = Array.isArray(res?.data?.items) ? res.data.items
                    : Array.isArray(res?.data) ? res.data : [];
                setStaffList(
                    items
                        .filter((s: any) => s?.id)
                        .map((s: any) => ({ id: Number(s.id), name: s.fullname || s.name || `#${s.id}` }))
                );
            })
            .catch(() => {})
            .finally(() => setLoadingStaff(false));
    }, []);

    const filtered = useMemo(() => {
        const kw = normalize(keyword.trim());
        if (!kw) return staffList;
        return staffList.filter(s => normalize(s.name).includes(kw));
    }, [staffList, keyword]);

    const toggle = (id: number, checked: boolean) => {
        if (checked) {
            onChange([...value, id]);
        } else {
            onChange(value.filter(v => v !== id));
        }
    };

    const handleOpenChange = (next: boolean) => {
        setOpen(next);
        // Đóng rồi mở lại thì thấy nguyên danh sách, không dính từ khóa cũ
        if (!next) setKeyword("");
    };

    const activeCount = value.length;

    const content = (
        <div className="w-[260px] bg-white dark:bg-[#1f1f1f] rounded-lg shadow-lg overflow-hidden">
            <div className="p-2 border-b border-gray-100 dark:border-[#303030]">
                <Input
                    size="small"
                    allowClear
                    autoFocus
                    prefix={<SearchOutlined className="text-gray-400" />}
                    placeholder={t("lead.filter_assign_search")}
                    value={keyword}
                    onChange={e => setKeyword(e.target.value)}
                />
            </div>

            <div className="max-h-[240px] overflow-y-auto p-3 space-y-2">
                {loadingStaff ? (
                    <div className="flex justify-center py-4"><Spin size="small" /></div>
                ) : filtered.length ? (
                    filtered.map(s => (
                        <Checkbox
                            key={s.id}
                            className="flex w-full"
                            checked={value.includes(s.id)}
                            onChange={e => toggle(s.id, e.target.checked)}
                        >
                            {s.name}
                        </Checkbox>
                    ))
                ) : (
                    <div className="py-4 text-center text-[12px] text-gray-400">
                        {t("lead.filter_assign_empty")}
                    </div>
                )}
            </div>

            {activeCount > 0 && (
                <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-gray-100 dark:border-[#303030]">
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">
                        {t("lead.filter_selected_count", { count: activeCount })}
                    </span>
                    <button
                        type="button"
                        className="text-[11px] !text-[#3e91ab] hover:underline"
                        onClick={() => onChange([])}
                    >
                        {t("lead.filter_clear_all")}
                    </button>
                </div>
            )}
        </div>
    );

    return (
        <Dropdown
            dropdownRender={() => content}
            trigger={["click"]}
            open={open}
            onOpenChange={handleOpenChange}
            getPopupContainer={() => document.body}
        >
            <div
                className={`flex items-center gap-1 px-3 py-1 rounded cursor-pointer transition text-000000e0 text-[14px]
                    ${open || activeCount > 0
                        ? "bg-blue-100 !text-[#3e91ab] dark:bg-[#2a2a2a]"
                        : "bg-gray-100 dark:bg-[#1f1f1f]"
                    }`}
            >
                {t("lead.filter_assign")}
                {activeCount > 0 && (
                    <span className="ml-1 bg-[#3e91ab] text-white text-[11px] rounded-full px-1.5 leading-tight">
                        {activeCount}
                    </span>
                )}
                <ChevronDown
                    size={16}
                    className={`transition-transform duration-200 ${open ? "rotate-90" : ""}`}
                />
            </div>
        </Dropdown>
    );
};

export default AssignFilter;
